import { useState } from "react";
import {
    Box,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseButton from "../../../Components/CloseButton";
import SeverityBadge from "../Badges/SeverityBadge";
import LevelBadge from "../Badges/LevelBadge";
import CartLoader from "../../../Helper/Loaders/CartLoader";

const StyledTableHead = styled(TableHead)(() => ({
    "& .MuiTableCell-root": {
        borderBottom: "1px solid #E8ECF0",
        backgroundColor: "#FFFFFF",
        whiteSpace: "nowrap",
    },
}));

const StyledTableBody = styled(TableBody)(() => ({
    "& .MuiTableRow-root .MuiTableCell-root": {
        borderBottom: "1px solid #F0F2F5",
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-root": {
        borderBottom: "none",
    },
}));

const COLUMN_WIDTHS = { severityName: "120px", severityLevelID: "90px" };

// Renderers keyed by `valueRenderingType`. Callers may override/extend via the
// `valueRenderers` prop. Columns with no type fall back to a default by field
// name (below), then to plain text.
const DEFAULT_VALUE_RENDERERS = {
    "severity-badge": (value) => <SeverityBadge severity={value} />,
    "level-badge": (value) => <LevelBadge level={value} />,
};

// Default render type for the known badge fields, so org meta can omit
// valueRenderingType for them (it's only needed to override the default).
const DEFAULT_TYPE_BY_FIELD = {
    severityName: "severity-badge",
    severityLevelID: "level-badge",
};

// A column names its field via `dataIndex` (preferred, matches the table-column
// convention) or the legacy `key`. Both resolve to the row property to read.
const getField = (col) => col.dataIndex ?? col.key;

/**
 * Reusable, domain-agnostic "View Condition" popup.
 *
 * It renders whatever `columns` (from page meta `viewConditions.data`) and
 * `rows` (pre-shaped by a domain adapter) it is given — it does NOT read page
 * meta or know about BMS faults vs platform alerts. Each surface supplies its
 * own meta + rows so the two never affect each other.
 *
 * @param {string}  title          - secondary line under the heading (e.g. alert name)
 * @param {string}  heading        - popup heading (default "Conditions")
 * @param {string}  triggerLabel   - clickable trigger text (default "View Condition")
 * @param {{dataIndex?:string,key?:string,label:string,valueRenderingType?:string}[]} columns
 * @param {object[]} rows          - objects keyed by the column `dataIndex`/`key`
 * @param {boolean} isVisible      - whether the trigger renders at all (meta-driven)
 * @param {boolean} isLoading
 * @param {Object<string,Function>} valueRenderers - optional renderers keyed by valueRenderingType
 */
const AlertConditionPopup = ({
    title,
    heading = "Conditions",
    triggerLabel = "View Condition",
    columns = [],
    rows = [],
    isVisible = false,
    isLoading = false,
    valueRenderers = DEFAULT_VALUE_RENDERERS,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    if (!isVisible || columns.length === 0) return null;

    const renderCell = (col, row) => {
        const field = getField(col);
        const type = col.valueRenderingType || DEFAULT_TYPE_BY_FIELD[field] || "text";
        const renderer = valueRenderers?.[type];
        if (renderer) return renderer(row[field], row);
        return (
            <Typography
                variant="fw400s12c686868"
                sx={{ lineHeight: "20px", display: "block", color: "#1A1A1A" }}
            >
                {row[field] || "—"}
            </Typography>
        );
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
                variant="fw500s12lh246D72F6"
                sx={{ cursor: "pointer", lineHeight: "20px", "&:hover": { color: "#4D44D8" } }}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                {triggerLabel}
            </Typography>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                    paper: {
                        sx: {
                            width: "860px",
                            maxWidth: "calc(100vw - 32px)",
                            borderRadius: "12px",
                            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
                            mt: "6px",
                            overflow: "hidden",
                        },
                    },
                }}
            >
                <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                        <Typography variant="fw600s16c000000" sx={{ lineHeight: "28px" }}>
                            {heading}
                        </Typography>
                        <CloseButton onClick={() => setAnchorEl(null)} size="small" />
                    </Box>
                    {title && (
                        <Typography
                            variant="fw400s12c686868"
                            sx={{ lineHeight: "20px", mt: 0.25, fontWeight: 500, color: "#1A1A1A", fontSize: "14px" }}
                        >
                            {title}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ overflow: "auto", maxHeight: "70vh", mx: 3, mb: 3 }}>
                    {isLoading ? (
                        <Box sx={{ py: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <CartLoader />
                        </Box>
                    ) : rows.length === 0 ? (
                        <Box sx={{ py: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Typography variant="fw400s14c8F8F8F" sx={{ color: "#87939D" }}>
                                No condition data available
                            </Typography>
                        </Box>
                    ) : (
                        <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
                            <StyledTableHead>
                                <TableRow>
                                    {columns.map((col, colIdx) => (
                                        <TableCell
                                            key={getField(col) ?? colIdx}
                                            sx={{
                                                padding: "10px 10px !important",
                                                width: COLUMN_WIDTHS[getField(col)] ?? "auto",
                                            }}
                                        >
                                            <Typography
                                                variant="fw400s12c686868"
                                                sx={{ lineHeight: "18px", fontWeight: 500, color: "#1A1A1A", fontSize: "14px" }}
                                            >
                                                {col.label}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </StyledTableHead>
                            <StyledTableBody>
                                {rows.map((row, idx) => (
                                    <TableRow key={idx}>
                                        {columns.map((col, colIdx) => (
                                            <TableCell
                                                key={getField(col) ?? colIdx}
                                                sx={{
                                                    padding: "10px 10px !important",
                                                    verticalAlign: "top",
                                                }}
                                            >
                                                {renderCell(col, row)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </StyledTableBody>
                        </Table>
                    )}
                </Box>
            </Popover>
        </Box>
    );
};

export default AlertConditionPopup;
